import {
    CANNOT_DELETE_ITEM_IN_USE_MSG,
    CloseButton,
    CrudMode,
    DELETE_WARNING,
    DropDownMenu,
    ListContext,
    ListContextValue,
    MenuItemType,
    Stack,
    Switch,
    Typography,
    UpdateButton,
    ValidationErrors,
    newToast,
} from "@compass/core";
import { DataModelInformation, SubmitButton } from "components";
import { DataModelEnum, Permission, PermissionAction, Class } from "generated/openApi";
import { useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { isAuthorized } from "services";
import ApiService from "services/apiService";

const extractGeneralMessage = (ex: any) => {
    const gen = ex?.response?.generalValidationErrors;
    if (Array.isArray(gen) && gen.length > 0) return gen.join(" ");
    const modelState = ex?.response?.errors;
    if (modelState && typeof modelState === "object") {
        const first = (Object.values(modelState) as any[]).flat().at(0);
        if (typeof first === "string") return first;
    }
    return "An error occurred while saving.";
};

export const ClassPageHeader = (props: {
    rootPermission: Permission;
    handle: string;
    crudMode: CrudMode;
    fetch: (id: number) => void;
    reroute: (id: number, crudMode: CrudMode) => void;
    classItem: Class;
    setClassItem: (classItem: Class) => void;
    initClassItem: Class;
    setValidationErrors: (errors: ValidationErrors) => void;
    unsavedChanges: boolean;
    resetUnsavedChanges: (items: any[]) => void;
    onValidate?: () => boolean;
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const listContext = useContext<ListContextValue>(ListContext);
    const id = props.classItem.id;
    const [createAnother, setCreateAnother] = useState<boolean>(false);


    const save = (classItem: Class) => {
        props.setValidationErrors({});

        if (props.onValidate && !props.onValidate()) return;

        if (id > 0) {
            ApiService.classes
                .saveUpdate(classItem)
                .then(() => {
                    props.fetch(id);
                    listContext.loadList?.();
                    dispatch(setAppToast(newToast("success")));
                    props.reroute(id, "view");
                })
                .catch((ex) => {
                    const msg = extractGeneralMessage(ex);
                    dispatch(setAppToast(newToast("error",msg)));
                    props.setValidationErrors(ex.data);
                });
        } else {
            ApiService.classes
                .saveCreate(classItem)
                .then((response) => {
                    const id = response;
                    if (createAnother) {
                        props.setClassItem(props.initClassItem);
                        props.resetUnsavedChanges([props.initClassItem]);
                    } else {
                        props.fetch(id);
                        props.reroute(id, "view");
                    }
                    listContext.loadList?.();
                    dispatch(setAppToast(newToast("success")));
                })
                .catch((ex) => {
                    const msg = extractGeneralMessage(ex);
                    dispatch(setAppToast(newToast("error", msg)));
                    props.setValidationErrors(ex.data);
                });
        }
    };

    const postDelete = (id: number) => {
        if (window.confirm(DELETE_WARNING)) {
            ApiService.classes.delete(id).then(() => {
                navigate("../");
                listContext.loadList?.();
                dispatch(setAppToast(newToast("success")));
            });
        }
    };

    const menuItems: MenuItemType[] = [];
    if (id > 0 && isAuthorized(props.rootPermission, PermissionAction.Delete)) {
        menuItems.push({
            label: <Typography color="error">Delete</Typography>,
            action: () => postDelete(id),
            disabled: props.classItem.isLocked,
            tooltip: props.classItem.isLocked ? CANNOT_DELETE_ITEM_IN_USE_MSG : undefined,

        });
    }

    return (
        <Stack direction="row" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing="sm">
                <Typography variant="inherit">
                    {id === 0 ? "New" : ""} {props.handle}
                </Typography>
                {menuItems.length > 0 && <DropDownMenu menuItems={menuItems} />}
            </Stack>

            <Stack direction="row" alignItems="center" spacing="sm">
                {id > 0 && (
                    <DataModelInformation dataModelType={DataModelEnum.Class} dataModelId={id} />
                )}

                {id === 0 && (
                    <Switch
                        value={createAnother}
                        onChange={(e) => setCreateAnother(e.target.checked)}
                        label="+ more"
                    />
                )}

                <CloseButton theme="Dark" confirm={props.unsavedChanges} />

                {props.crudMode === "view" &&
                    isAuthorized(props.rootPermission, PermissionAction.Update) && (
                        <UpdateButton theme="Dark" onClick={() => props.reroute(id, "edit")} />
                    )}

                {props.crudMode === "edit" && (
                    <SubmitButton
                        theme="Dark"
                        onClick={() => save(props.classItem)}
                        disabled={!props.unsavedChanges}
                    />
                )}
            </Stack>
        </Stack>
    );
};
